import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { BrandLogoComponent } from '../../shared/brand-logo.component';
import { LanguageSelectorComponent } from '../../shared/language-selector.component';
import { SearchModalsComponent } from '../../shared/search-modals.component';
import { AuthTokenService } from '../../core/auth/auth-token.service';
import { initialsOf } from '../../shared/tile.util';

@Component({
  selector: 'gg-marketplace-layout',
  imports: [
    RouterOutlet,
    TranslocoModule,
    BrandLogoComponent,
    LanguageSelectorComponent,
    SearchModalsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div style="min-height:100vh">
      <header
        style="position:sticky;top:0;z-index:40;background:rgba(246,243,236,.85);backdrop-filter:blur(12px);border-bottom:1px solid #e7e1d5"
      >
        <div style="max-width:1200px;margin:0 auto;padding:13px 24px;display:flex;align-items:center;gap:26px">
          <a (click)="goHome()" style="display:block"><gg-brand-logo [size]="18" /></a>
          <nav style="display:flex;gap:4px;margin-left:8px" class="gg-hidemob">
            <span class="nav-link" style="font-size:14px;font-weight:600;color:#4a5365;padding:7px 12px;border-radius:9px" (click)="goSearch()">{{ 'nav.explore' | transloco }}</span>
            <span class="nav-link" style="font-size:14px;font-weight:600;color:#4a5365;padding:7px 12px;border-radius:9px" (click)="goHosts()">{{ 'nav.hosts' | transloco }}</span>
            <span class="nav-link" style="font-size:14px;font-weight:600;color:#4a5365;padding:7px 12px;border-radius:9px" (click)="goTrips()">{{ 'nav.trips' | transloco }}</span>
          </nav>
          <div style="margin-left:auto;display:flex;align-items:center;gap:14px">
            <span class="gg-hidemob" style="font-size:13px;font-weight:600;color:#4a5365;cursor:pointer">{{ 'common.currency' | transloco }}</span>
            <gg-language-selector />
            <span class="gg-hidemob" style="font-size:13.5px;font-weight:600;color:#16213c;cursor:pointer;text-decoration:underline;text-underline-offset:3px;text-decoration-color:#e0d3b6">{{ 'nav.arePm' | transloco }}</span>
            <div style="position:relative">
              <div
                style="display:flex;align-items:center;gap:9px;border:1px solid #e2dccf;background:#fff;border-radius:999px;padding:5px 6px 5px 13px;cursor:pointer"
                (click)="toggleMenu()"
              >
                <div style="display:flex;flex-direction:column;gap:3px">
                  <div style="width:15px;height:2px;background:#16213c;border-radius:2px"></div>
                  <div style="width:15px;height:2px;background:#16213c;border-radius:2px"></div>
                </div>
                <div style="width:28px;height:28px;border-radius:50%;background:#16213c;color:#fff;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center">{{ avatar() }}</div>
              </div>
              @if (menuOpen()) {
                <div class="gg-acct-menu">
                  @if (auth.isAuthenticated()) {
                    <div class="gg-acct-menu__head">
                      <div class="gg-acct-menu__name">{{ auth.user()?.displayName || auth.user()?.email }}</div>
                      <div class="gg-acct-menu__mail">{{ auth.user()?.email }}</div>
                    </div>
                    <button type="button" (click)="goTrips()">{{ 'nav.trips' | transloco }}</button>
                    <button type="button" (click)="logout()">{{ 'auth.logout' | transloco }}</button>
                  } @else {
                    <button type="button" (click)="goLogin()">{{ 'auth.login' | transloco }}</button>
                    <button type="button" (click)="goRegister()">{{ 'auth.createAccount' | transloco }}</button>
                  }
                </div>
              }
            </div>
          </div>
        </div>
      </header>

      <router-outlet />
      <gg-search-modals />
    </div>
  `,
  styles: [
    `
      .gg-acct-menu {
        position: absolute; right: 0; top: calc(100% + 8px); min-width: 200px; z-index: 50;
        background: #fff; border: 1px solid #e7ddc9; border-radius: 14px; padding: 6px;
        box-shadow: 0 22px 60px -28px rgba(28, 36, 52, 0.55);
      }
      .gg-acct-menu__head { padding: 9px 11px 11px; border-bottom: 1px solid #efe8da; margin-bottom: 4px; }
      .gg-acct-menu__name { font-size: 13.5px; font-weight: 700; color: #16213c; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .gg-acct-menu__mail { font-size: 12px; color: #8a93a3; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .gg-acct-menu button {
        display: block; width: 100%; text-align: left; appearance: none; border: 0; background: transparent;
        font: inherit; font-size: 13.5px; font-weight: 600; color: #3a4250; padding: 9px 11px; border-radius: 9px; cursor: pointer;
      }
      .gg-acct-menu button:hover { background: #f6f2ea; color: #16213c; }
    `,
  ],
})
export class MarketplaceLayoutComponent {
  private readonly router = inject(Router);
  readonly auth = inject(AuthTokenService);

  readonly menuOpen = signal(false);
  readonly avatar = computed(() => {
    const user = this.auth.user();
    if (!user) return 'G';
    return initialsOf(user.displayName || user.email || 'G');
  });

  toggleMenu(): void {
    this.menuOpen.update((open) => !open);
  }

  goHome(): void {
    this.router.navigate(['/']);
  }
  goSearch(): void {
    this.router.navigate(['/search']);
  }
  goHosts(): void {
    this.router.navigate(['/search']);
  }
  goTrips(): void {
    this.menuOpen.set(false);
    this.router.navigate(['/trips']);
  }
  goLogin(): void {
    this.menuOpen.set(false);
    this.router.navigate(['/login']);
  }
  goRegister(): void {
    this.menuOpen.set(false);
    this.router.navigate(['/login'], { queryParams: { mode: 'register' } });
  }
  async logout(): Promise<void> {
    this.menuOpen.set(false);
    await this.auth.logout();
    this.router.navigate(['/']);
  }
}
